import { NavItem } from "@/shared/components/Header/types";

/**
 * Recursively filters a navigation configuration array based on a user's permissions.
 *
 * @param navConfig The base navigation configuration array.
 * @param userPermissions An array of strings representing the user's permissions.
 * @returns A new, deeply-filtered navigation array containing only accessible items and sections.
 */
export const getAccessibleNavConfig = (
  navConfig: NavItem[],
  userPermissions: string[]
): NavItem[] => {
  return navConfig.reduce((acc, item) => {
    if (item.divider) {
      acc.push(item);
      return acc;
    }
    // If the item is a section with a submenu, filter its children recursively.
    if (item.submenu) {
      const accessibleSubmenu = getAccessibleNavConfig(item.submenu, userPermissions);

      // If the submenu is not empty after filtering, add the section with the filtered submenu.
      if (accessibleSubmenu.length > 0) {
        acc.push({ ...item, submenu: accessibleSubmenu });
      }
    } else {
      // If the item is a direct link, check for permissions.
      // Include the item if it has no permission property or if the user has the required permission.
      if (
        !item.permission ||
        userPermissions.includes(item.permission) ||
        item.allowWithoutPermission
      ) {
        acc.push(item);
      }
    }
    return acc;
  }, [] as NavItem[]);
};
